import { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Clock, CheckCircle2, MessageSquare, BarChart3, Headphones, Loader2 } from "lucide-react";
import ImmovoxLogo from "@/components/ImmovoxLogo";
import { useUtmParams, buildCalendlyUrlWithUtm } from "@/hooks/useUtmParams";
import { Analytics } from "@/lib/analytics";
import "@/types/calendly.d.ts";

interface CalendlyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

interface CalendlyEvent {
  event: string;
  payload?: {
    event?: {
      uri?: string;
    };
    invitee?: {
      uri?: string;
      email?: string;
      name?: string;
    };
  };
}

const demoPoints = [
  { icon: CheckCircle2, text: "Découvrez le filtrage intelligent des appels" },
  { icon: MessageSquare, text: "Voyez comment les tickets sont qualifiés" },
  { icon: BarChart3, text: "Explorez le dashboard temps réel" },
  { icon: Headphones, text: "Testez l'expérience avec votre voix" },
];

const CalendlyModal = ({ open, onOpenChange, source = "unknown" }: CalendlyModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const utmParams = useUtmParams();
  const openedAtRef = useRef<number | null>(null);

  // Handle Calendly events
  const handleCalendlyMessage = useCallback((e: MessageEvent) => {
    // Only process Calendly events
    if (!e.data?.event?.startsWith("calendly")) return;

    const calendlyEvent = e.data as CalendlyEvent;

    switch (calendlyEvent.event) {
      case "calendly.date_and_time_selected":
        // User selected a date/time
        Analytics.calendlyDateSelected(source);
        break;

      case "calendly.event_scheduled":
        // 🎉 CONVERSION: Demo successfully booked!
        const utmRecord: Record<string, string> = {};
        Object.entries(utmParams).forEach(([k, v]) => {
          if (v) utmRecord[k] = v;
        });
        Analytics.demoBooked(source, utmRecord);
        console.log("[Calendly] Demo booked!", calendlyEvent.payload);
        break;

      case "calendly.event_type_viewed":
        // Widget loaded and event type is shown
        break;
    }
  }, [source, utmParams]);

  useEffect(() => {
    if (open) {
      // Track modal opening and record timestamp
      openedAtRef.current = Date.now();
      Analytics.calendlyModalOpened(source);
      
      // Listen for Calendly postMessage events
      window.addEventListener("message", handleCalendlyMessage);
      
      setIsLoading(true);
      const timer = setTimeout(() => {
        const container = document.getElementById("calendly-inline-container");
        if (container && window.Calendly) {
          container.innerHTML = "";
          
          // Build Calendly URL with UTM parameters
          const baseUrl = "https://calendly.com/alex-kalyvox/30min?hide_event_type_details=1&hide_gdpr_banner=1";
          const calendlyUrl = buildCalendlyUrlWithUtm(baseUrl, utmParams);
          
          window.Calendly.initInlineWidget({
            url: calendlyUrl,
            parentElement: container,
          });
        }
      }, 100);

      // Hide loader after widget has time to load
      const loadTimer = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => {
        clearTimeout(timer);
        clearTimeout(loadTimer);
        window.removeEventListener("message", handleCalendlyMessage);
        
        // Track modal closed with time spent
        if (openedAtRef.current) {
          const timeSpent = Date.now() - openedAtRef.current;
          Analytics.calendlyModalClosed(source, timeSpent);
        }
      };
    }
  }, [open, source, handleCalendlyMessage, utmParams]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[680px]">
          {/* Left Column - Context */}
          <div className="bg-secondary p-8 flex flex-col">
            {/* Logo & Brand - Centered vertically */}
            <div className="flex items-center gap-4 mb-8">
              <ImmovoxLogo className="h-10 w-auto text-primary flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                L'assistant vocal pour l'immobilier
              </p>
            </div>

            {/* Duration Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium w-fit mb-8">
              <Clock className="w-4 h-4" />
              30 minutes
            </div>

            {/* Separator */}
            <div className="h-px bg-border mb-8" />

            {/* Demo Points */}
            <div className="space-y-5 flex-1">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Ce que vous découvrirez
              </h3>
              {demoPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <point.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground/80 leading-relaxed">
                    {point.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer note */}
            <p className="text-xs text-muted-foreground mt-8">
              Sans engagement · Démo personnalisée
            </p>
          </div>

          {/* Right Column - Calendly Widget */}
          <div className="bg-white relative">
            {/* Loading State */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground">Chargement du calendrier...</p>
                </div>
              </div>
            )}
            <div 
              id="calendly-inline-container" 
              className="calendly-inline-widget w-full h-full" 
              style={{ minHeight: "680px" }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendlyModal;
